// Central site configuration — pricing and integration keys live here so they
// are a single-line change rather than scattered through components.

/**
 * Web3Forms access key for the Contact form.
 *
 * This key is registered against info@stratumlabs.tech (GitHub issue #41). It is
 * a Web3Forms *access* key, which is designed to be public and live in
 * client-side code — it is not a secret API key. See
 * https://docs.web3forms.com/getting-started/faq ("Access key is public").
 */
export const WEB3FORMS_ACCESS_KEY = '6e5055f4-b84e-4772-b812-f414fdff8af1'

/**
 * The placeholder value used before a real key is registered. Typed as `string`
 * (not the literal) so the comparison in `isWeb3FormsConfigured` stays valid
 * even when `WEB3FORMS_ACCESS_KEY` is a concrete literal.
 */
const WEB3FORMS_PLACEHOLDER_KEY: string = 'YOUR_WEB3FORMS_ACCESS_KEY'

/** True when a real Web3Forms key has been configured (not the placeholder). */
export const isWeb3FormsConfigured = (): boolean =>
  WEB3FORMS_ACCESS_KEY.trim().length > 0 && WEB3FORMS_ACCESS_KEY !== WEB3FORMS_PLACEHOLDER_KEY

/** Primary contact email for the business. */
export const CONTACT_EMAIL = 'info@stratumlabs.tech'

// ─── Talynth product launch ──────────────────────────────────────────────────

/** The Talynth product site URL (used once the product is live). */
export const TALYNTH_URL = 'https://talynth.tech'

/**
 * Whether the Talynth product is live yet.
 *
 * While `false` (pre-launch): outbound links to talynth.tech are disabled
 * (rendered as a non-clickable "Coming soon" state) and the launch banner is
 * shown on the Home and Products pages. **At launch, flip this to `true`** —
 * that single change re-enables the real links and hides the banner, with no
 * other code edits needed.
 */
export const TALYNTH_LIVE = true

/** Short banner copy shown (on Home + Products) while Talynth is pre-launch. */
export const TALYNTH_LAUNCH_BANNER =
  'Talynth is launching next week. The platform isn\u2019t live yet \u2014 ' +
  'register your interest via Contact and we\u2019ll let you know the moment it goes live.'

// ─── Site-wide notice (build-time; e.g. maintenance / info / warning) ─────────

/**
 * Visual/semantic category of a site notice. Drives colour + icon + default
 * ARIA role:
 * - `info`        — neutral informational message.
 * - `warning`     — attention needed, non-urgent.
 * - `maintenance` — scheduled/ongoing maintenance (typically pinned).
 */
export type SiteNoticeType = 'info' | 'warning' | 'maintenance'

export interface SiteNotice {
  /** Master on/off. When false, nothing renders. */
  enabled: boolean
  /**
   * Stable identifier for THIS notice. Used as the dismissal key, so publishing
   * a **new** notice (new `id`) re-shows even for visitors who dismissed a prior
   * one. Bump it whenever the message materially changes.
   */
  id: string
  /** Notice category — styling + default icon/role. */
  type: SiteNoticeType
  /**
   * `true` = pinned: sticky and **non-dismissible** (use for maintenance /
   * must-see). `false` = dismissible: the visitor can close it, and the
   * dismissal is remembered for the session (per `id`).
   */
  pinned: boolean
  /** Required banner message. */
  message: string
  /** Optional bold lead-in before the message (e.g. "Scheduled maintenance"). */
  title?: string
  /** Optional call-to-action link (e.g. a status page or Contact). */
  link?: { href: string; label: string }
}

/**
 * The current site-wide notice. **Build-time only** — this is a static S3 +
 * CloudFront site with no backend/SSM; changing a notice is a one-line edit →
 * PR → deploy (same model as `TALYNTH_LIVE`). Do NOT wire this to runtime
 * config.
 *
 * To publish a notice: set `enabled: true`, give it a fresh `id`, pick a `type`,
 * decide `pinned` (maintenance → usually pinned/non-dismissible). To clear it:
 * `enabled: false`.
 */
export const SITE_NOTICE: SiteNotice = {
  enabled: false,
  id: 'none',
  type: 'info',
  pinned: false,
  message: '',
}

/** Session-storage key prefix for remembering a dismissed (unpinned) notice. */
export const SITE_NOTICE_DISMISS_KEY = 'sl-site-notice-dismissed'

/** Whether a notice should actually render (enabled + has a message). */
export const isSiteNoticeActive = (n: SiteNotice = SITE_NOTICE): boolean =>
  n.enabled && n.message.trim().length > 0

/** Website-as-a-Service pricing tiers (South African Rand, per month). */
export interface ServiceTier {
  name: string
  priceMonthly: number
  tagline: string
  features: string[]
  featured?: boolean
}

/** The three sellable service lines (shown as a co-equal triad). */
export interface ServiceLine {
  icon: string
  title: string
  summary: string
  priceLabel: string
}

export const SERVICE_LINES: ServiceLine[] = [
  {
    icon: '🌐',
    title: 'Website-as-a-Service',
    summary:
      'We build, host, and maintain your website for one monthly fee — no big upfront cost. Live fast, always looked after.',
    priceLabel: 'From R500 / month',
  },
  {
    icon: '🛠️',
    title: 'Bespoke development',
    summary:
      'Custom web and software built around your workflow — cloud-native, AI-assisted, well-tested, and yours to own.',
    priceLabel: 'Quoted per project',
  },
  {
    icon: '🧭',
    title: 'Consulting',
    summary:
      'A second opinion or hands-on help on architecture, cloud, or AI adoption — for existing projects or new ones.',
    priceLabel: 'Quoted per engagement',
  },
]

export const WAAS_TIERS: ServiceTier[] = [
  {
    name: 'Basic',
    priceMonthly: 500,
    tagline: 'A professional web presence, fully managed.',
    features: [
      'Up to 5 pages, mobile-first responsive design',
      'AI-assisted copywriting to get you live faster',
      'Managed hosting on AWS (S3 + CloudFront, HTTPS included)',
      'Domain setup and a business email alias',
      'Monthly content updates and maintenance',
      'Basic analytics',
    ],
  },
  {
    name: 'Advanced',
    priceMonthly: 1200,
    tagline: 'A larger site with richer functionality and integrations.',
    featured: true,
    features: [
      'Everything in Basic',
      'Up to 15 pages plus a blog or news section',
      'Contact forms, bookings, or lightweight integrations',
      'AI features baked in (chat, content generation, automation)',
      'Priority updates and faster turnaround',
      'Enhanced analytics and SEO setup',
    ],
  },
]

/** Enquiry categories offered on the Contact page. */
export const ENQUIRY_CATEGORIES = [
  'Website-as-a-Service (Basic / Advanced)',
  'Custom website build (once-off quote)',
  'Custom software development',
  'Project consulting',
  'Self-hosting / site ownership transfer',
  'Talynth (POPIA compliance product)',
  'General enquiry',
] as const

/** A capability ("what we do") with a dedicated depth page at /capabilities/:slug. */
export interface Capability {
  slug: string
  icon: string
  title: string
  summary: string
  intro: string
  points: { heading: string; body: string }[]
}

export const CAPABILITIES: Capability[] = [
  {
    slug: 'cloud',
    icon: '☁️',
    title: 'Cloud-based solutions',
    summary:
      'Scalable, secure, cost-efficient systems built on cloud infrastructure — reliability and room to grow without running servers yourself.',
    intro:
      'We design and run software on modern cloud infrastructure so you get enterprise-grade reliability, security, and scale without the cost and burden of managing servers. Our own products run this way — including on near-zero-idle-cost architectures — and we bring the same discipline to your systems.',
    points: [
      {
        heading: 'Serverless and managed-first',
        body: 'We favour managed and serverless services (static hosting, CDNs, managed databases, functions) so there is less to patch, less to break, and you pay for what you use rather than idle capacity.',
      },
      {
        heading: 'Secure by default',
        body: 'Private storage, TLS everywhere, least-privilege access, and infrastructure-as-code so every change is reviewed and reproducible — not clicked together by hand in a console.',
      },
      {
        heading: 'Cost-aware architecture',
        body: 'We size infrastructure to real usage and lean on free-tier and pay-per-use services where they fit, so hosting stays cheap at low volume and scales predictably as you grow.',
      },
      {
        heading: 'Observability built in',
        body: 'Logging, metrics, and alerts from day one, so problems surface before your customers notice them.',
      },
    ],
  },
  {
    slug: 'custom-development',
    icon: '🛠️',
    title: 'Custom development',
    summary:
      'Bespoke web and software built around your actual workflow — not a template forced to fit. Clean, well-tested, and maintainable.',
    intro:
      'When off-the-shelf tools do not fit, we build software around how your business actually works. We write clean, well-tested, maintainable code and treat your codebase as something you own — documented, handed over, and yours to keep.',
    points: [
      {
        heading: 'Built around your workflow',
        body: 'We start from the problem and the people using the tool, not from a template, so the result fits how you actually operate.',
      },
      {
        heading: 'Tested and maintainable',
        body: 'Automated tests, type safety, and continuous integration are standard — so changes are safe and the system stays healthy over time.',
      },
      {
        heading: 'Integrations and automation',
        body: 'We connect the tools you already use (accounting, CRM, payments, email) and automate the manual steps between them.',
      },
      {
        heading: 'You own it',
        body: 'Clear handover, documentation, and the option to take full ownership or self-host — no lock-in. (See the Services page for ownership transfer.)',
      },
    ],
  },
  {
    slug: 'ai',
    icon: '🤖',
    title: 'AI-first by default',
    summary:
      'We lean heavily on AI across our products and builds to automate repetitive work, so your team gets more output for less manual effort.',
    intro:
      'AI is not a bolt-on for us — it is woven through how we build and what we ship. We use it to automate the repetitive, judgement-light work that eats your team\u2019s time, so people focus on the work that actually needs them.',
    points: [
      {
        heading: 'Automation of the grind',
        body: 'Document generation, data extraction, triage, drafting, and summarisation — the repetitive tasks that scale badly with people but well with AI.',
      },
      {
        heading: 'AI features in your product',
        body: 'Chat assistants, content generation, classification, and smart search built into the software we deliver, where they add real value rather than novelty.',
      },
      {
        heading: 'Human-in-the-loop by design',
        body: 'AI drafts and suggests; people review and decide on anything that matters. We build the checkpoints in so you stay in control.',
      },
      {
        heading: 'Proven in our own products',
        body: 'Talynth uses AI for evidence verification and document drafting in a regulated, compliance-critical setting — we apply the same care to your build.',
      },
    ],
  },
]

export const getCapability = (slug: string): Capability | undefined =>
  CAPABILITIES.find((c) => c.slug === slug)
